import * as React from 'react';
import { translate } from 'components/translate-hoc';
import { ConfigContext } from 'modules/context';
import AnalyticsService from 'modules/analytics-provider/analytics-provider.service';
import PlatformList from '../platform-list';
import RedemptionMainError from '../redemption-platform-error';
import Button from 'components/button';
import Spinner from 'components/spinner/spinner.component';

import { getPlatformFromURL, getProfilePlatformById } from '../../redemption.utils';

import { ICodeFirstPlatformSelectionProps } from './сode-first-platform-selection.types';
import { IProfilePlatform } from 'types/profiles';

import { PLATFORM_TYPES } from '@sitegen/common/constants/platforms';

import { TEXT_KEYS } from '../redemption-platform-selection.constants';
import {
    ANALYTICS_FLOW_EVENT_PAGE_DATA,
    ANALYTICS_FLOW_EVENT_SUBMIT_CODE_DATA
} from './code-first-platform-selection.constants';

import '../redemption-platform-selection.styles.less';

export class CodeFirstPlatformSelection extends React.Component<
ICodeFirstPlatformSelectionProps,
ICodeFirstPlatformSelectionState
> {
    public static contextType = ConfigContext;

    public state: ICodeFirstPlatformSelectionState = {
        activeProfilePlatform: getProfilePlatformById(
            this.props.preselectedPlatformId,
            this.props.profilePlatforms
        ),
        showError: false
    };

    public async componentDidMount() {
        AnalyticsService.sendFlowEvent(ANALYTICS_FLOW_EVENT_PAGE_DATA);

        await this.tryAutosubmitPlatform();
    }

    public async tryAutosubmitPlatform() {
        let autosubmitPlatform: PLATFORM_TYPES | null = null;
        const platformFromURL = this.getPlatformIDFromURL();

        if (platformFromURL) {
            autosubmitPlatform = PLATFORM_TYPES[platformFromURL];
        }

        if (this.props.availablePlatforms.length === 1) {
            autosubmitPlatform = this.props.availablePlatforms[0];
        }

        if (autosubmitPlatform) {
            const profilePlatform = getProfilePlatformById(autosubmitPlatform, this.props.profilePlatforms) as IProfilePlatform;
            await this.selectPlatform(profilePlatform);
            const wasLinked: boolean = !!(profilePlatform && profilePlatform.profileId);

            if (profilePlatform && wasLinked) {
                this.getMyGiftsHandler();
            }
        }
    }

    public getPlatformIDFromURL(): PLATFORM_TYPES | null {
        const { availablePlatforms } = this.props;
        const platformId = getPlatformFromURL();

        // Make sure the one we parsed from URL is valid
        return availablePlatforms.find(platform => platform === platformId) || null;
    }

    public selectPlatform = async (profilePlatform: IProfilePlatform): Promise<void> => {
        await this.setStateAsync({
            activeProfilePlatform: profilePlatform,
            showError: false
        });
    };

    public setStateAsync = (newState: ICodeFirstPlatformSelectionState) =>
        new Promise(resolve => this.setState(newState, resolve));

    public getMyGiftsHandler = (): void => {
        const { activeProfilePlatform } = this.state;
        const {
            code,
            isAdditionalInfoRequired,
            redeemGift,
            savePlatformType
        } = this.props;

        if (!activeProfilePlatform) {
            return this.setState({ showError: true });
        }

        AnalyticsService.sendFlowEvent({
            ...ANALYTICS_FLOW_EVENT_SUBMIT_CODE_DATA,
            platformSelection: activeProfilePlatform.id
        });

        const platform = PLATFORM_TYPES[activeProfilePlatform.id];

        return isAdditionalInfoRequired
            ? savePlatformType(platform)
            : redeemGift({ platform, code });
    };

    public renderPageContents = (): JSX.Element => {
        const { dangerouslyTranslateWithHTML: translateKey } = this.props;
        const { activeProfilePlatform } = this.state;
        const { getModeClass } = this.context;

        const activePlatformId = activeProfilePlatform && activeProfilePlatform.id;
        const buttonText: string = translateKey(TEXT_KEYS.REDEEM_MAIN_PAGE_BUTTON);
        const titleText: string = translateKey(TEXT_KEYS.REDEEM_MAIN_PAGE_TITLE);
        return (
            <>
                <div className={getModeClass('sitegen-redemption-platform-selection__title')}>
                    <p>{titleText}</p>
                </div>

                <PlatformList
                    platformsList={this.props.availablePlatforms}
                    profilePlatforms={this.props.profilePlatforms}
                    activePlatformId={activePlatformId}
                    onSelectPlatform={this.selectPlatform}
                />

                <Button onClick={this.getMyGiftsHandler}>{buttonText}</Button>

                <RedemptionMainError
                    showError={this.state.showError}
                    activeProfilePlatform={activeProfilePlatform}
                    isCodeValid
                />
            </>
        );
    };

    public render() {
        return (
            <div className="sitegen-redemption-platform-selection">
                {this.props.fetching ? <Spinner /> : this.renderPageContents()}
            </div>
        );
    }
}

interface ICodeFirstPlatformSelectionState {
    readonly showError: boolean;
    readonly activeProfilePlatform: IProfilePlatform | null;
}

export default translate()(CodeFirstPlatformSelection);
